import { useState, useEffect, useRef } from 'react';
import './ClubInfoBox.css';
import memberIcon from '../../assets/icons/memberIcon.png';
import moreIcon from '../../assets/icons/moreIcon.png';
import { supabase } from '../../lib/supabaseClient.js';
import ClubApplyForm from './ClubApplyForm.jsx';
import { useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';

export default function ClubInfoBox({ clubId, onDelete }) {
    const menuRef = useRef(null);

    const [club, setClub] = useState(null);
    const [showApplyForm, setShowApplyForm] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showRecruitModal, setShowRecruitModal] = useState(false);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [loginUser, setLoginUser] = useState(null);
    const [memberRole, setMemberRole] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        getClub();
    }, [clubId]);

    useEffect(() => {
        fetchUserAndRole();
    }, [clubId]);

    useEffect(() => {
        const interval = setInterval(() => {
            getClub();
            fetchUserAndRole();
        }, 2000);

        return () => clearInterval(interval);
    }, [clubId]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    async function fetchUserAndRole() {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            setLoginUser(null);
            setMemberRole(null);
            return;
        }

        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('email', user.email)
            .maybeSingle();

        if (userError) {
            console.error("사용자 조회 실패:", userError.message);
            setLoginUser(null);
            setMemberRole(null);
            return;
        }

        setLoginUser(userData);

        if (!userData) {
            setMemberRole(null);
            return;
        }

        const { data: memberData, error: memberError } = await supabase
            .from('club_members')
            .select('role')
            .eq('club_id', clubId)
            .eq('user_id', userData.id)
            .maybeSingle();

        if (memberError) {
            console.error("동아리 역할 조회 실패:", memberError.message);
            setMemberRole(null);
            return;
        }

        setMemberRole(memberData?.role ?? null);
    }

    async function getRealClubInfo() {
        const { data: membersData, error: membersError } = await supabase
            .from("club_members")
            .select(`
                user_id,
                role,
                user:user_id (
                    id,
                    name
                )
            `)
            .eq("club_id", clubId);

        if (membersError) {
            console.error("실제 동아리원 정보 조회 실패:", membersError.message);
            return {
                realMemberCount: null,
                realLeaderId: null,
                realLeaderName: null,
            };
        }

        const members = membersData || [];
        const leader = members.find((member) => member.role === "leader");

        return {
            realMemberCount: members.length,
            realLeaderId: leader?.user_id ?? null,
            realLeaderName: leader?.user?.name ?? null,
        };
    }

    async function getClub() {
        const { data, error } = await supabase
            .from('clubs')
            .select('*')
            .eq('id', clubId)
            .maybeSingle();

        if (error) {
            console.log(error);
            return;
        }

        if (!data) {
            onDelete?.();
            return;
        }

        if (data.recruitment_start && data.recruitment_end) {
            const today = new Date();
            const start = new Date(data.recruitment_start);
            const end = new Date(data.recruitment_end);

            today.setHours(0, 0, 0, 0);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            const newStatus =
                today >= start && today <= end
                    ? "모집 중"
                    : "모집 종료";

            if (newStatus !== data.recruitment_status) {
                await supabase
                    .from("clubs")
                    .update({ recruitment_status: newStatus })
                    .eq("id", data.id);

                data.recruitment_status = newStatus;
            }
        }

        const {
            realMemberCount,
            realLeaderId,
            realLeaderName,
        } = await getRealClubInfo();

        if (realMemberCount !== null) {
            data.member_count = realMemberCount;
        }

        if (realLeaderId) {
            data.leader_id = realLeaderId;
        }

        data.leader = {
            name: realLeaderName || "동아리장 없음",
        };

        setClub(data);
    }

    function handleMoreClick() {
        if (!loginUser) {
            alert('로그인을 해주세요!');
            return;
        }

        if (!memberRole) return;

        setShowMenu(!showMenu);
    }

    async function handleLeaveClub() {
        if (!loginUser) {
            alert("로그인이 필요합니다.");
            return;
        }

        if (memberRole !== "member") {
            alert("동아리장 또는 비회원은 탈퇴할 수 없습니다.");
            return;
        }

        const isConfirm = window.confirm("정말 이 동아리에서 탈퇴하시겠습니까?");
        if (!isConfirm) return;

        const { error: leaveError } = await supabase
            .from("club_members")
            .delete()
            .eq("club_id", clubId)
            .eq("user_id", loginUser.id)
            .eq("role", "member");

        if (leaveError) {
            console.error("동아리 탈퇴 실패:", leaveError.message);
            alert("동아리 탈퇴에 실패했습니다.");
            return;
        }

        const {
            realMemberCount,
            realLeaderId,
            realLeaderName,
        } = await getRealClubInfo();

        await supabase
            .from("clubs")
            .update({
                member_count: realMemberCount ?? 0,
                leader_id: realLeaderId,
            })
            .eq("id", clubId);

        alert("동아리에서 탈퇴되었습니다.");

        setClub((prev) =>
            prev
                ? {
                    ...prev,
                    member_count: realMemberCount ?? 0,
                    leader_id: realLeaderId,
                    leader: {
                        name: realLeaderName || "동아리장 없음",
                    },
                }
                : prev
        );

        setMemberRole(null);
        setShowMenu(false);

        setTimeout(async () => {
            await getClub();
            await fetchUserAndRole();
        }, 300);
    }

    async function saveRecruitPeriod() {
        if (!startDate || !endDate) {
            alert("시작일과 종료일을 모두 선택해주세요.");
            return;
        }

        const today = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        today.setHours(0, 0, 0, 0);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const status =
            today >= start && today <= end
                ? "모집 중"
                : "모집 종료";

        const { error } = await supabase
            .from("clubs")
            .update({
                recruitment_start: startDate,
                recruitment_end: endDate,
                recruitment_status: status,
            })
            .eq("id", club.id);

        if (error) {
            console.log(error);
            alert("모집 기간 등록 실패");
            return;
        }

        alert("모집 기간이 등록되었습니다.");

        setShowRecruitModal(false);
        setShowMenu(false);

        await getClub();
        await fetchUserAndRole();
    }

    if (!club) return <div>로딩중...</div>;

    return (
        <>
            <div className="ClubInfoBox">
                <div className="club-image">
                    {club.image_url ? (
                        <img
                            src={club.image_url}
                            alt={club.name}
                            className="club-image-img"
                        />
                    ) : (
                        club.name
                    )}
                </div>

                <div className="club-header">
                    <div className="club-left">
                        <div className="club-title-row">
                            <h1 className="club-name">{club.name}</h1>

                            <span className={club.recruitment_status === "모집 종료" ? "club-closed" : "club-recruiting"}>
                                {club.recruitment_status}
                            </span>

                            <div className="club-more-wrap" ref={menuRef}>
                                <button className="club-more-btn" onClick={handleMoreClick}>
                                    <img src={moreIcon} alt="더보기" />
                                </button>

                                {showMenu && (
                                    <div className="club-more-menu">
                                        <button onClick={() => navigate(`/clubs/${clubId}/members`)}>
                                            동아리원 보기
                                        </button>

                                        {memberRole === 'member' && (
                                            <button onClick={handleLeaveClub}>
                                                동아리 탈퇴
                                            </button>
                                        )}

                                        {memberRole === 'leader' && (
                                            <>
                                                <button onClick={() => navigate(`/clubs/${clubId}/setting`)}>
                                                    동아리 설정
                                                </button>

                                                <button onClick={() => setShowRecruitModal(true)}>
                                                    모집 기간 등록
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="club-description">{club.description}</p>
                    </div>

                    <div className="club-right">
                        <button
                            className={`club-join ${club.recruitment_status === "모집 종료" || memberRole ? "disabled" : ""}`}
                            onClick={() => {
                                if (!loginUser) {
                                    alert('로그인을 해주세요!');
                                    return;
                                }

                                if (memberRole) return;

                                if (club.recruitment_status === "모집 종료") return;

                                setShowApplyForm(true);
                            }}
                            disabled={club.recruitment_status === "모집 종료" || !!memberRole}
                        >
                            {memberRole ? '가입 완료' : '가입하기'}
                        </button>
                    </div>
                </div>

                <div className="club-bottom">
                    <div className="club-item">
                        <div className="club-title">
                            <img src={memberIcon} alt="회원 수 아이콘" className="member-icon" />
                            <p>회원 수</p>
                        </div>
                        <strong>{club.member_count}명</strong>
                    </div>

                    <div className="club-item">
                        <p>동아리 장</p>
                        <strong>{club.leader?.name}</strong>
                    </div>

                    <div className="club-item">
                        <p>회비</p>
                        <strong>{club.fee}</strong>
                    </div>

                    <div className="club-item">
                        <p>지원 자격</p>
                        <ul className="club-condition">
                            {club.eligibility?.split('/').map((item, index) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {showApplyForm && (
                <ClubApplyForm
                    club={club}
                    onClose={() => setShowApplyForm(false)}
                />
            )}

            {showRecruitModal && (
                <div className="recruitModal-bg">
                    <div className="recruitModal">
                        <button
                            className="recruitModal-close"
                            onClick={() => setShowRecruitModal(false)}
                        >
                            ×
                        </button>

                        <h2>모집 기간 등록</h2>

                        <div className="recruitModal-row">
                            <label>시작일</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="recruitModal-row">
                            <label>종료일</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <button
                            className="recruitModal-submit"
                            onClick={saveRecruitPeriod}
                        >
                            완료
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}